import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Button, Box, Skeleton } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { ArrowBack } from '@mui/icons-material';
import { actions as ConnectionActions } from '../../../Ducks/Connection.duck';
import ValidationSummaryResultCard from '../../../Components/ValidationSummaryResultCard';
import {
  groupResultsByEventType,
  groupSummariesByEventType,
  getAllEventTypes,
} from '../../../../Utils/Services/ValidationRunService/validationRunService';
import { useBreadcrumb } from 'app/components/Breadcrumbs';
import { formatDate } from 'app/utils/formatter/dateTimeFormatter';

const ValidationRunDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { connectionId, employerId, validationRunId } = useParams();

  const { validationRunDetails } = useSelector(state => state.ConnectionReducer);
  const validationRun = validationRunDetails?.item;
  const loading = validationRunDetails?.loading || false;
  const loaded = validationRunDetails?.loaded || false;

  useEffect(() => {
    dispatch(ConnectionActions.validationRunDetails.requestData({ validationRunId }));
  }, [dispatch, validationRunId]);

  useBreadcrumb('validationRuns.detail.title');

  const handleBack = () => {
    navigate(`/employers/${employerId}/connections/${connectionId}/validation-runs`);
  };

  if (loading || !loaded) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={200} />
        </Grid>
      </Grid>
    );
  }

  if (!validationRun) {
    return (
      <Typography color="error">
        <FormattedMessage id="validationRuns.detail.notFound" />
      </Typography>
    );
  }

  const groupedResults = groupResultsByEventType(validationRun.results);
  const groupedSummaries = groupSummariesByEventType(validationRun.summaries);
  const eventTypes = getAllEventTypes(validationRun.summaries, validationRun.results);

  return (
    <Grid container spacing={3}>
      {/* Header with Back Button */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mr: 2 }}>
            <FormattedMessage id="button.back" />
          </Button>
        </Box>
      </Grid>

      {/* Overview Statistics */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  <FormattedMessage id="validationRuns.detail.createdAt" />
                </Typography>
                <Typography variant="body1">{formatDate(validationRun.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  <FormattedMessage id="validationRuns.detail.totalMutations" />
                </Typography>
                <Typography variant="body1">{validationRun.totalMutations}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h6" gutterBottom>
                  <FormattedMessage id="validationRuns.detail.totalEvents" />
                </Typography>
                <Typography variant="body1">{validationRun.totalEvents}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Event Type Columns */}
      {eventTypes.map(eventType => (
        <Grid item xs={12} md={4} key={eventType}>
          <ValidationSummaryResultCard
            eventType={eventType}
            summary={groupedSummaries[eventType]}
            results={groupedResults[eventType] || []}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default ValidationRunDetails;
